import _ from 'lodash';
import React from 'react';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { Badge, DataTable, Icon, SkeletonDisplayText, Tooltip } from '@shopify/polaris';
import { LockMinor, ReportMinor, SettingsMajor } from '@shopify/polaris-icons';

import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';
import ProcessAddonV4 from 'src/components/views/subscription-v4/addon/process-addon';
import ChangePlanFromReviewIOModal from 'src/components/views/common/change-plan-from-reviewio-modal';

import { routes } from 'src/routes';
import history from 'src/routes/history';
import { allowUsingNewSearchSettings, getAISearchStatus } from 'src/helpers/search-settings-helper';
import {
  ADDONS_INFO,
  ADDONS_INFO_V4,
  AI_POWERED_SEARCH_NAME,
  SUBSCRIPTION_STATE
} from 'src/constants/subscription';
import { URL } from 'src/constants/links';
import { checkUseSearchPersonalization } from 'src/helpers/subscription-v3-helper';
import * as subscriptionV3Actions from 'actions/subscription-v3-actions';
import * as searchControlActions from 'actions/search-control-action';
import * as productPerformanceRankingAction from 'actions/product-performance-ranking-action';
import * as searchPersonalizationAction from 'actions/search-personalization-action';
import * as SemanticActions from 'actions/semantic-action';
import bookmarkIcon from 'src/assets/images/common/bookmark.svg';
import FlatIcon from 'src/assets/images/common/flat-linear-icon.png';
import { checkPersonalizationCampaignRemainingDays } from 'src/helpers';

const SearchControlTable = props => {
  const {
    i18n,
    aiSearchStatus,
    addonLoading,
    addonStatus,
    statusStore,
    useBoltOn,
    selectedPlan,
    searchFeatures,
    performanceRankingSettings,
    personalizationSettings,
    searchV2Setting,
    getAddonStatus,
    getFeaturesStatus,
    tenantFeatures,
    notAllowAddon,
    enabledSubscriptionV4,
    enabledSearchMerchandising,
    enabledManualSynonym,
    semanticSetting,
    getSemanticSettings,
    getPerformanceRankingSettings,
    isUsePersonalization,
    getPersonalizationSettings,
    enableAISearch,
    semanticLoading
  } = props;
  const [openAddon, setOpenAddon] = React.useState(false);
  const [changePlanFromReviewIOModal, setChangePlanFromReviewIOModal] = React.useState(false);

  React.useEffect(() => {
    getFeaturesStatus();
    getSemanticSettings();
    getPerformanceRankingSettings();
    if (isUsePersonalization) getPersonalizationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const semanticStatus = semanticSetting.enable ? 'on' : 'off';
  const rankingStatus = _.get(performanceRankingSettings, 'enabled', false) ? 'on' : 'off';
  const personalizationStatus = _.get(personalizationSettings, 'enabled', false) ? 'on' : 'off';

  const addonRemainingCycles = _.get(addonStatus, 'trialInfo.remainingDays', 14);
  const isSubscribeNow =
    !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0;

  const getAIAction = route => {
    if (
      aiSearchStatus === SUBSCRIPTION_STATE.TRIAL ||
      aiSearchStatus === SUBSCRIPTION_STATE.UNLOCK
    ) {
      return {
        onSetting: () => {
          history.push(route);
        }
      };
    }

    const actionAI = () => {
      if (selectedPlan.includes('freemium')) {
        history.push(routes.subscription.package());
        return;
      }
      if (!notAllowAddon) {
        if (useBoltOn) setChangePlanFromReviewIOModal(true);
        else setOpenAddon(true);
      }
    };

    return {
      settingButton: (
        <Tooltip
          preferredPosition="mostSpace"
          content={
            <>
              <p>{i18n.translate(`AdminHTML.SearchControl.Plan.unlockTooltip_Content01`)}</p>
              <p>{i18n.translate(`AdminHTML.SearchControl.Plan.unlockTooltip_Content02`)}</p>
            </>
          }>
          <div
            className={`unlock-button ${notAllowAddon ? 'unlock-button-disabled' : ''}`}
            onClick={() => actionAI()}>
            <Icon source={LockMinor} />
            {i18n.translate('AdminHTML.Common.Action.unLock')}
          </div>
        </Tooltip>
      )
    };
  };

  const getAction = action => {
    if (action) {
      return action;
    }

    return {
      settingButton: (
        <Tooltip
          preferredPosition="above"
          content={
            <>
              <p>{i18n.translate(`AdminHTML.SearchControl.Plan.unlockTooltip_Content01`)}</p>
              <p>{i18n.translate(`AdminHTML.SearchControl.Plan.unlockTooltip_Content02`)}</p>
            </>
          }>
          <div
            className={`unlock-button ${notAllowAddon ? 'unlock-button-disabled' : ''}`}
            onClick={() =>
              useBoltOn
                ? setChangePlanFromReviewIOModal(true)
                : history.push(routes.subscription.package())
            }>
            <Icon source={LockMinor} />
            {i18n.translate('AdminHTML.Common.Action.unLock')}
          </div>
        </Tooltip>
      )
    };
  };

  const AIBadge = (
    <Badge>
      <img width={14} height={14} src={FlatIcon} alt="wand" />
      <span>{i18n.translate('AdminHTML.AdvanceSearch.ai')}</span>
    </Badge>
  );

  const aiLockRows = [
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.searchAddonName'),
      titleAdditional: AIBadge,
      type: {
        text: i18n.translate('AdminHTML.MainMenu.advancedSearch'),
        status: 'warning'
      },
      status: aiSearchStatus,
      helpDoc: URL.AI_Powered_Search,
      ...getAIAction('')
    }
  ];

  const aiUnlockRows = [
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.productPerformanceRanking'),
      titleAdditional: AIBadge,
      type: {
        text: i18n.translate('AdminHTML.MainMenu.advancedSearch'),
        status: 'warning'
      },
      status: aiSearchStatus === SUBSCRIPTION_STATE.UNLOCK ? rankingStatus : aiSearchStatus,
      helpDoc: URL.Link_Search_Product_Performance_Ranking_Guide,
      ...getAIAction(routes.productPerformanceRanking.view())
    },
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.semantic'),
      titleAdditional: AIBadge,
      type: {
        text: i18n.translate('AdminHTML.MainMenu.advancedSearch'),
        status: 'warning'
      },
      status: aiSearchStatus === SUBSCRIPTION_STATE.UNLOCK ? semanticStatus : aiSearchStatus,
      helpDoc: URL.Link_Search_Semantic_ManualGuide,
      ...getAIAction(routes.semanticSearch.view())
    },
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.aiSearch'),
      titleAdditional: AIBadge,
      type: {
        text: i18n.translate('AdminHTML.MainMenu.advancedSearch'),
        status: 'warning'
      },
      status:
        aiSearchStatus === SUBSCRIPTION_STATE.UNLOCK ? searchFeatures.aiSynonym : aiSearchStatus,
      helpDoc: URL.Link_V2_Search_Synonym_ManualGuide,
      ...getAIAction(routes.aiSynonym.view())
    }
  ];

  const rawRows = [
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.manualSynonyms'),
      type: {
        text: i18n.translate('AdminHTML.MainMenu.standardSearch'),
        status: 'new'
      },
      helpDoc: URL.Link_V1_Search_Synonym_ManualGuide,
      status: enabledManualSynonym ? searchFeatures.synonym || 'on' : SUBSCRIPTION_STATE.LOCKED,
      ...getAction(
        enabledManualSynonym
          ? {
              onSetting: () => {
                history.push(routes.synonym.view());
              }
            }
          : null
      )
    },
    {
      title: i18n.translate('AdminHTML.SearchSettings.SearchFields.titleV2'),
      type: {
        text: i18n.translate('AdminHTML.MainMenu.standardSearch'),
        status: 'new'
      },
      status: searchFeatures.searchable || 'on',
      helpDoc: URL.Search_Control_Search_Field,
      onSetting: () => {
        history.push(routes.relevanceSettings.view());
      }
    },
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.partialSearch'),
      type: {
        text: i18n.translate('AdminHTML.MainMenu.standardSearch'),
        status: 'new'
      },
      helpDoc: URL.Search_Control_Partial_Search,
      status: 'on'
    },
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.typoTolerance'),
      type: {
        text: i18n.translate('AdminHTML.MainMenu.standardSearch'),
        status: 'new'
      },
      helpDoc: URL.Search_Control_Typo,
      status: tenantFeatures.typoTolerance ? 'on' : 'off'
    },
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.specialCharacters'),
      type: {
        text: i18n.translate('AdminHTML.MainMenu.standardSearch'),
        status: 'new'
      },
      helpDoc: URL.Search_Control_Special_Character,
      status: searchV2Setting.specialCharacter || 'on'
    },
    {
      title: i18n.translate('AdminHTML.SearchControl.Table.Features.searchMerchandising'),
      type: {
        text: i18n.translate('AdminHTML.SearchControl.Table.Type.merchandising'),
        status: 'info'
      },
      status: enabledSearchMerchandising
        ? searchFeatures.merchandising || 'on'
        : SUBSCRIPTION_STATE.LOCKED,
      helpDoc: URL.Search_Control_Merchandising,
      ...getAction(
        enabledSearchMerchandising
          ? {
              onSetting: () => {
                history.push(routes.merchandising.view());
              }
            }
          : null
      )
    }
  ];

  const getStatusBadge = status => {
    if (status === 'on' || status === SUBSCRIPTION_STATE.UNLOCK) {
      return {
        status: 'success',
        name: 'status_On'
      };
    }

    if (status === SUBSCRIPTION_STATE.TRIAL) {
      return {
        status: 'warning',
        name: 'status_Trial'
      };
    }

    if (status === SUBSCRIPTION_STATE.LOCKED || status === SUBSCRIPTION_STATE.NOT_TRIAL) {
      return {
        status: 'warning',
        name: 'status_Locked'
      };
    }

    if (status === SUBSCRIPTION_STATE.EXPIRED) {
      return {
        status: 'new',
        name: 'status_Expired'
      };
    }

    return {
      status: 'new',
      name: `status_Off`
    };
  };

  const getTableRows = () => {
    let rows = rawRows;
    if (![SUBSCRIPTION_STATE.TRIAL, SUBSCRIPTION_STATE.UNLOCK].includes(aiSearchStatus)) {
      rows = aiLockRows.concat(rawRows);
    } else {
      rows = aiUnlockRows.concat(rawRows);
    }

    if (enableAISearch) {
      rows.unshift({
        title: i18n.translate('AdminHTML.SearchControl.Table.Features.searchPersonalization'),
        titleAdditional: (
          <>
            {AIBadge}
            <Badge>Beta</Badge>
          </>
        ),
        type: {
          text: i18n.translate('AdminHTML.MainMenu.advancedSearch'),
          status: 'warning'
        },
        status: isUsePersonalization ? personalizationStatus : SUBSCRIPTION_STATE.LOCKED,

        helpDoc: URL.Link_Search_Personalization_Guide,
        ...getAction(
          isUsePersonalization
            ? {
                onSetting: () => {
                  history.push(routes.searchPersonalization.view());
                }
              }
            : null
        )
      });
    }

    if (searchFeatures.featureGetting || semanticLoading) {
      return rows.map(row => {
        return [
          <div className="feature-wrapper">
            {row.title}
            {row.titleAdditional && row.titleAdditional}
          </div>,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ];
      });
    }

    const isSubscribeAddon = _.get(addonStatus, 'subscribed', false);
    const remainingDays = checkPersonalizationCampaignRemainingDays();
    return rows.map(row => {
      const statusBadge = getStatusBadge((row.status || '').toLowerCase());

      return [
        <div className="feature-wrapper">
          {row.title}
          {row.titleAdditional && row.titleAdditional}
        </div>,
        <div className="type-wrapper">
          <Badge status={row.type.status}>
            <span>{row.type.text}</span>
          </Badge>
        </div>,
        row.status?.toLowerCase() === 'on' &&
        remainingDays !== -1 &&
        (row.title ===
          i18n.translate('AdminHTML.SearchControl.Table.Features.searchPersonalization') ||
          (!addonLoading &&
            !isSubscribeAddon &&
            row.type.text === i18n.translate('AdminHTML.MainMenu.advancedSearch'))) ? (
          <div className="BC-MarketingCampaign_remainingDays">
            <Badge status={remainingDays > 5 ? 'attention' : 'critical'}>
              {i18n.translate('AdminHTML.MarketingCampaign.Features.remainingDays', {
                count: remainingDays,
                nt_number: remainingDays
              })}
            </Badge>
          </div>
        ) : (
          <Badge progress="complete" status={statusBadge.status}>
            {i18n.translate(`AdminHTML.Common.Common.${statusBadge.name}`)}
          </Badge>
        ),

        <div className="flex-align-center action">
          {row.settingButton || (
            <>
              <Tooltip
                preferredPosition="mostSpace"
                content={i18n.translate(
                  `AdminHTML.SearchControl.Table.Tooltip.${row.onSetting ? 'setting' : 'noSetting'}`
                )}>
                <div
                  className={`wrap-icon settings-icon ${row.onSetting ? '' : 'wrap-icon-disabled'}`}
                  onClick={() => row.onSetting && row.onSetting()}>
                  <Icon source={SettingsMajor} color="base" />
                </div>
              </Tooltip>
              <Tooltip
                preferredPosition="mostSpace"
                content={i18n.translate('AdminHTML.SearchControl.Table.Tooltip.analytic')}>
                <div
                  className={`wrap-icon`}
                  onClick={() => history.push(routes.analytics.overviewSearch())}>
                  <Icon source={ReportMinor} color="base" />
                </div>
              </Tooltip>
            </>
          )}
        </div>,
        <div className="flex-align-center action">
          <Tooltip
            preferredPosition="mostSpace"
            content={i18n.translate(
              `AdminHTML.SearchControl.Table.Tooltip.${row.helpDoc ? 'userGuide' : 'noUserGuide'}`
            )}>
            <div
              className={`wrap-icon ${row.helpDoc ? '' : 'wrap-icon-disabled'}`}
              onClick={() => {
                row.helpDoc && window.open(row.helpDoc, '_blank');
              }}>
              <img alt="learn-more" src={bookmarkIcon} />
            </div>
          </Tooltip>
        </div>
      ];
    });
  };

  return (
    <div className="table-features">
      <DataTable
        verticalAlign="middle"
        columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
        headings={[
          i18n.translate('AdminHTML.SearchControl.Table.Column.feature'),
          i18n.translate('AdminHTML.Common.Columns.type'),
          i18n.translate('AdminHTML.Common.Columns.status'),
          <div className="align-center">{i18n.translate('AdminHTML.Common.Action.actions')}</div>,
          <div className="align-center">{i18n.translate('AdminHTML.Common.Action.learnMore')}</div>
        ]}
        rows={getTableRows()}
      />

      {enabledSubscriptionV4 ? (
        <ProcessAddonV4
          openModalReview={openAddon}
          addon={ADDONS_INFO_V4.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
          hadSubscribed={isSubscribeNow}
          handleAfterSubscribeAddon={() => getAddonStatus(AI_POWERED_SEARCH_NAME)}
          handleReviewModal={() => setOpenAddon(false)}
        />
      ) : (
        <ProcessAddon
          openModalReview={openAddon}
          addon={ADDONS_INFO.find(e => e.addonName === AI_POWERED_SEARCH_NAME)}
          hadSubscribed={isSubscribeNow}
          handleAfterSubscribeAddon={() => getAddonStatus(AI_POWERED_SEARCH_NAME)}
          handleReviewModal={() => setOpenAddon(false)}
        />
      )}
      <ChangePlanFromReviewIOModal
        open={changePlanFromReviewIOModal}
        onClose={() => setChangePlanFromReviewIOModal(false)}
      />
    </div>
  );
};

const mapStateToProps = state => {
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const isNewSearch = allowUsingNewSearchSettings(searchVersion);
  const enableAISearch = _.get(
    state.oauth.tenantApp,
    'settings.instantSearch.settings.AI-synonyms',
    false
  );
  const statusStore = _.get(state.oauth, 'subscription.status', '');
  const addonStatus = _.get(state.subscriptionV3, `addonStatus.${AI_POWERED_SEARCH_NAME}`, {});
  const searchV2Setting = _.get(state.oauth.tenantApp, 'settings.searchV2.settings', {});
  const tenantFeatures = _.get(state.oauth.tenantApp, 'features', {});
  const performanceRankingSettings = _.get(state.productPerformanceRanking, 'settings', {});
  const personalizationSettings = _.get(state.searchPersonalization, 'settings', {});
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const isUsePersonalization = checkUseSearchPersonalization(tenantApp);
  return {
    isNewSearch,
    isUsePersonalization,
    aiSearchStatus: getAISearchStatus(enableAISearch, statusStore, addonStatus),
    statusStore,
    addonStatus,
    useBoltOn: _.get(state.oauth, 'subscription.useBoltOn', false),
    addonLoading: _.get(state.subscriptionV3, 'loading', false),
    searchFeatures: state.searchControl,
    searchV2Setting,
    tenantFeatures,
    enableAISearch,
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    semanticSetting: _.get(state.semantic, 'settings', {}),
    performanceRankingSettings,
    personalizationSettings,
    semanticLoading: state.semantic.settingGetting,
    enabledSearchMerchandising: _.get(
      state.oauth.tenantApp,
      'settings.visualMerchandising.settings.allow-feature',
      true
    ),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan', ''),
    enabledManualSynonym: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.manual-synonyms',
      true
    )
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  getFeaturesStatus: () => dispatch(searchControlActions.getSearchControlFeatureStatus()),
  getSemanticSettings: () => dispatch(SemanticActions.getSemanticSettings()),
  getPerformanceRankingSettings: () => dispatch(productPerformanceRankingAction.getSettings()),
  getPersonalizationSettings: () => dispatch(searchPersonalizationAction.getSettings())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SearchControlTable));
